/**
 * Arquivo que já está na base (spec 2026-10-22-kb-arquivo-repetido-e-atalho, §3): o envio
 * não sobe cópia. Diz onde o original está e, quando ele está em outra pasta, oferece
 * "Criar atalho aqui" ou "Mover o original para cá". O atalho não libera leitura (A2), e
 * mover usa a rota de hoje (`PATCH /documents/{id}`, trilha `kb.document.move`).
 */
import { useState, type ReactNode } from 'react'
import { Alert, Anchor, Button, Group, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconCopy, IconFolderShare, IconFolderSymlink } from '@tabler/icons-react'
import { criarKbAtalho, updateKbDocument, type KbDocumentoExistente, type KbVerificacao } from '@/api/kb'
import { mensagemDeErro } from './acesso'
import { bloqueioDeMover } from './arrastar'
import { nomeDaPasta } from './envio'

/** As consultas que mudam quando um atalho nasce ou um original muda de pasta. */
export const CONSULTAS_DA_PASTA = ['kb-folders', 'kb-documents', 'kb-folder', 'kb-folder-detalhes'] as const

/** "Req1 e passa a seguir…", ou "da pasta onde está" quando a pessoa não acessa a pasta dele. */
export function textoDeMover(d: KbDocumentoExistente): string {
  const de = d.caminho ? `sai de ${nomeDaPasta(d.caminho)}` : 'sai da pasta onde está'
  return `${d.titulo} ${de} e passa a seguir o acesso desta pasta.`
}

export function podeMoverOriginal(v: KbVerificacao): boolean {
  return v.situacao === 'outra_pasta' && !!v.documento && bloqueioDeMover(v.documento) === null
}

/** Onde o original está: caminho com link quando a pessoa navega até lá. */
function OndeEsta({ d, compacto, onAbrirPasta }: { d: KbDocumentoExistente; compacto: boolean; onAbrirPasta?: (id: number) => void }) {
  if (!d.caminho) {
    return <Text size="sm">{compacto ? 'Já está na base, numa pasta que você não acessa.' : 'Este arquivo já está na base, numa pasta que você não acessa.'}</Text>
  }
  const pastaId = d.pasta_id
  const caminho: ReactNode = onAbrirPasta && pastaId !== null
    ? <Anchor component="button" type="button" size="sm" onClick={() => onAbrirPasta(pastaId)}>{d.caminho}</Anchor>
    : <Text span size="sm" fw={600}>{d.caminho}</Text>
  return <Text size="sm">{compacto ? 'Já está em ' : 'Este arquivo já está em '}{caminho}</Text>
}

interface Props {
  verificacao: KbVerificacao
  /** Pasta do envio, onde o atalho nasce ou para onde o original vem */
  pastaId: number
  /** Linha de uma lista de vários: texto curto, botões pequenos e sem "Cancelar" */
  compacto?: boolean
  onAbrirPasta?: (id: number) => void
  onCancelar?: () => void
  onResolvido: (como: 'atalho' | 'movido') => void
}

export function AvisoDeRepetido({ verificacao: v, pastaId, compacto = false, onAbrirPasta, onCancelar, onResolvido }: Props) {
  const qc = useQueryClient()
  const [confirmandoMover, setConfirmandoMover] = useState(false)
  const d = v.documento
  const aoMudar = () => { for (const k of CONSULTAS_DA_PASTA) qc.invalidateQueries({ queryKey: [k] }) }
  const aoFalhar = (e: unknown) =>
    notifications.show({ color: 'red', title: 'Não foi possível concluir', message: mensagemDeErro(e, 'Falha na operação.') })

  const atalho = useMutation({
    mutationFn: () => criarKbAtalho(d!.id, pastaId),
    onSuccess: () => {
      aoMudar()
      notifications.show({ color: 'teal', title: 'Atalho criado', message: d!.titulo })
      onResolvido('atalho')
    },
    onError: aoFalhar,
  })
  const mover = useMutation({
    mutationFn: () => updateKbDocument(d!.id, { folder_id: pastaId }),
    onSuccess: () => {
      aoMudar()
      notifications.show({ color: 'teal', title: 'Documento movido', message: d!.titulo })
      onResolvido('movido')
    },
    onError: aoFalhar,
  })
  const ocupado = atalho.isPending || mover.isPending
  const tamanho = compacto ? 'compact-xs' : 'sm'

  if (v.situacao === 'mesma_pasta') {
    const texto = <>{compacto ? 'Já está nesta pasta: ' : 'Este arquivo já está nesta pasta: '}<strong>{d?.titulo}</strong></>
    return compacto ? <Text size="xs">{texto}</Text> : <Alert color="gray" icon={<IconCopy size={16} />}><Text size="sm">{texto}</Text></Alert>
  }
  if (v.situacao === 'atalho_na_pasta') {
    const texto = 'Esta pasta já tem um atalho para este arquivo.'
    return compacto ? <Text size="xs">{texto}</Text> : <Alert color="gray" icon={<IconFolderSymlink size={16} />}><Text size="sm">{texto}</Text></Alert>
  }
  if (v.situacao !== 'outra_pasta' || !d) return null

  const corpo = (
    <Stack gap="xs">
      <Stack gap={0}>
        <OndeEsta d={d} compacto={compacto} onAbrirPasta={onAbrirPasta} />
        {!compacto && <Text size="xs" c="dimmed">{d.titulo}</Text>}
      </Stack>
      {confirmandoMover ? (
        <Stack gap={6}>
          <Text size="sm">{textoDeMover(d)}</Text>
          <Group gap="xs">
            <Button size={tamanho} leftSection={<IconFolderShare size={14} />} loading={mover.isPending} onClick={() => mover.mutate()}>
              Mover para cá
            </Button>
            <Button size={tamanho} variant="default" disabled={mover.isPending} onClick={() => setConfirmandoMover(false)}>Voltar</Button>
          </Group>
        </Stack>
      ) : (
        <Group gap="xs">
          <Button size={tamanho} leftSection={<IconFolderSymlink size={14} />} loading={atalho.isPending} disabled={ocupado} onClick={() => atalho.mutate()}>
            Criar atalho aqui
          </Button>
          {podeMoverOriginal(v) && (
            <Button size={tamanho} variant="default" leftSection={<IconFolderShare size={14} />} disabled={ocupado} onClick={() => setConfirmandoMover(true)}>
              Mover o original para cá
            </Button>
          )}
          {onCancelar && <Button size={tamanho} variant="subtle" color="gray" disabled={ocupado} onClick={onCancelar}>Cancelar</Button>}
        </Group>
      )}
    </Stack>
  )
  return compacto ? corpo : <Alert color="yellow" icon={<IconCopy size={16} />} title="Este arquivo já está na base">{corpo}</Alert>
}
